import React from 'react';
import PropTypes from 'prop-types';
import { gettext, translationPropType } from "@eb/i18n";
import classNames from 'classnames';
import { NOTIFICATION_OPTIONS_PROP_TYPES } from '@eb/eds-notification';
import { SIZES as BUTTON_SIZES } from '@eb/eds-button';
import { ICON_PROP_TYPE } from '@eb/eds-icon-button';

import { IconButton } from '@eb/eds-icon-button';

import noop from 'lodash/noop';
import FocusTrap from 'focus-trap-react';
import { onClickOutsideHOC } from '@eb/eds-utils';
import {
    addWindowListener,
    removeWindowListener,
    HAS_DOCUMENT,
} from '@eb/eds-utils';
import * as constants from './constants';
import CollapsiblePaneLayout from './CollapsiblePaneLayout';
import ModalBody from './ModalBody';

import './modal.scss';

import { CrossChunky } from '@eb/eds-iconography';

const CloseButton = ({ onClose, hasBackgroundColor = false }) => {
    let component = null;

    if (onClose) {
        component = (
            <div className="eds-modal__close-button">
                <IconButton
                    data-automation="modal-close-button"
                    data-spec="close-button"
                    onClick={onClose}
                    iconType={<CrossChunky />}
                    title={gettext('Close')}
                    isOverImage={hasBackgroundColor}
                />
            </div>
        );
    }

    return component;
};

const DecoratedModalBody = onClickOutsideHOC(
    ({
        children,
        onClose,
        headerActionIconType,
        headerActionTitle,
        onHeaderAction,
        title,
        secondaryTitle,
        illustration,
        pane,
        isPaneOpen,
        primarySize,
        primaryType,
        primaryText,
        primaryIsDisabled,
        onPrimaryAction,
        secondaryType,
        secondaryText,
        secondaryIsDisabled,
        onSecondaryAction,
        tertiaryNode,
        type,
        onTertiaryAction,
        dangerouslySetContainerClassName,
        modalBodyIsEmpty,
        notificationOptions,
        buttonBarMessage,
        ariaLabel,
        ariaLabelledby,
        ariaDescribedby,
        ariaModal,
        primaryIsLoading,
    }) => {
        let dangerousClassName;

        if (dangerouslySetContainerClassName) {
            dangerousClassName = dangerouslySetContainerClassName.__className;
        }

        const className = classNames(
            'eds-modal__body',
            'eds-fx--grow',
            dangerousClassName,
        );

        let modalBody = (
            <CollapsiblePaneLayout
                isPaneOpen={isPaneOpen}
                pane={pane}
                content={
                    <ModalBody
                        headerActionIconType={headerActionIconType}
                        headerActionTitle={headerActionTitle}
                        onHeaderAction={onHeaderAction}
                        title={title}
                        secondaryTitle={secondaryTitle}
                        illustration={illustration}
                        primarySize={primarySize}
                        primaryType={primaryType}
                        primaryText={primaryText}
                        primaryIsDisabled={primaryIsDisabled}
                        primaryIsLoading={primaryIsLoading}
                        onPrimaryAction={onPrimaryAction}
                        secondaryType={secondaryType}
                        secondaryText={secondaryText}
                        secondaryIsDisabled={secondaryIsDisabled}
                        onSecondaryAction={onSecondaryAction}
                        tertiaryNode={tertiaryNode}
                        onTertiaryAction={onTertiaryAction}
                        notificationOptions={notificationOptions}
                        buttonBarMessage={buttonBarMessage}
                        ariaLabel={ariaLabel}
                        ariaLabelledby={ariaLabelledby}
                        ariaDescribedby={ariaDescribedby}
                        ariaModal={ariaModal}
                    >
                        {children}
                    </ModalBody>
                }
            />
        );

        if (modalBodyIsEmpty) {
            modalBody = children;
        }

        return (
            <div
                className={className}
                aria-hidden="false"
                data-spec="eds-modal-body"
            >
                {modalBody}
                {onClose && (
                    <CloseButton
                        onClose={onClose}
                        hasBackgroundColor={type === constants.TYPE_APERTURE}
                    />
                )}
            </div>
        );
    },
);

export default class Modal extends React.PureComponent {
    static propTypes = {
        /**
         * The contents to display in the model
         */
        children: constants.MODAL_CHILDREN_PROPTYPE.isRequired,
        /**
         * The type of modal that should be shown
         */
        type: PropTypes.oneOf(constants.TYPES),
        /**
         * A function with the desired behavior on mount (componentDidMount)
         */
        onMount: PropTypes.func,
        /**
         * Is the dialog currently shown?
         */
        isShown: PropTypes.bool,
        /**
         * Removes min height for simple Modals
         */
        noMinHeight: PropTypes.bool,
        /**
         * Is the dialog closable?
         */
        isClosable: PropTypes.bool,
        /**
         * Removes padding on the modal container
         */
        noPadding: PropTypes.bool,
        /**
         * Add a class to the modals wrapper <dialog>
         */
        dangerouslySetContainerClassName: PropTypes.shape({
            __className: PropTypes.string,
        }),
        /**
         * A function with the desired behavior on close
         */
        onClose: PropTypes.func,
        /**
         * The icon to use for the secondary header action
         */
        headerActionIconType: ICON_PROP_TYPE,
        /**
         * The title for the secondary header action icon
         */
        headerActionTitle: translationPropType,
        /**
         * A function with the desired behavior on clicking the secondary header action icon
         */
        onHeaderAction: PropTypes.func,
        /**
         * The title displayed above the content
         */
        title: constants.MODAL_TITLE_PROPTYPE,
        /**
         * The title displayed above the content
         */
        secondaryTitle: PropTypes.node,
        /**
         * The illustration displayed inside the modal
         */
        illustration: PropTypes.node,
        /**
         * The type of button that should be shown for primary button
         */
        primaryType: PropTypes.string,
        /**
         * The text that should be shown across the primary button
         */
        primaryText: PropTypes.node,
        /**
         * The size of the primary button
         */
        primarySize: PropTypes.oneOf([...BUTTON_SIZES]),
        /**
         * Whether the primary button should be disabled
         */
        primaryIsDisabled: PropTypes.bool,
        /**
         * The function that should be triggered on primary button click
         */
        onPrimaryAction: PropTypes.func,
        /**
         * The type of button that should be shown for secondary button
         */
        secondaryType: PropTypes.string,
        /**
         * The text that should be shown across the secondary button
         */
        secondaryText: PropTypes.node,
        /**
         * Whether the secondary button should be disabled
         */
        secondaryIsDisabled: PropTypes.bool,
        /**
         * The function that should be triggered on secondary button click
         */
        onSecondaryAction: PropTypes.func,
        /**
         * The node that will act as a tertiary action
         */
        tertiaryNode: PropTypes.node,
        /**
         * The function that should be triggered on tertiary node click
         */
        onTertiaryAction: PropTypes.func,
        /**
         * A message to show above the button bar
         */
        buttonBarMessage: PropTypes.string,
        /**
         * True if the modal body should be empty, i.e. not include the header, footer, or body containers
         */
        modalBodyIsEmpty: PropTypes.bool,
        /**
         * Disables the ability to close the modal by clicking the outside overlay
         */
        disableCloseOnClickOutside: PropTypes.bool,
        /**
         * Used to render notifications inside the modal header.
         */
        notificationOptions: PropTypes.oneOfType([
            NOTIFICATION_OPTIONS_PROP_TYPES,
            PropTypes.arrayOf(NOTIFICATION_OPTIONS_PROP_TYPES),
        ]),
        /**
         * Changes the role attribute to dialog
         */
        roleIsDialog: PropTypes.bool,
        /**
         * The content to display next to the pane
         */
        content: PropTypes.node,
        /**
         * The content to display inside the pane, if any
         */
        pane: PropTypes.node,
        /**
         * Whether pane is open or closed
         */
        isPaneOpen: PropTypes.bool,
        /**
         *  Defines a string value that labels the current element
         */
        ariaLabel: PropTypes.string,
        /**
         *  Identifies the element (or elements) that labels the current element
         */
        ariaLabelledby: PropTypes.string,
        /**
         *  Identifies the element (or elements) that describes the the current element
         */
        ariaDescribedby: PropTypes.string,
        /**
         *  Indicates whether an element is modal when displayed
         */
        ariaModal: PropTypes.bool,
        /**
         * Whether the primary button should be displayed with a spinner
         */
        primaryIsLoading: PropTypes.bool,
    };

    static defaultProps = {
        type: constants.TYPE_SIMPLE,
        isShown: false,
        noPadding: false,
        roleIsDialog: false,
    };

    componentDidMount() {
        const { isShown } = this.props;

        this._boundHandleEscKey = this._handleEscKey.bind(this);
        this._checkShownState(isShown);
    }

    componentWillReceiveProps({ isShown }) {
        this._checkShownState(isShown);
    }

    componentWillUnmount() {
        this._unattachWindowListeners();
        this._lockModalViewport(false);
    }

    // used to lock the viewport so that the user can scroll under the modal
    _lockModalViewport(isLocked) {
        if (HAS_DOCUMENT) {
            const lockClass = 'eds-modal--hidden-overflow';

            if (isLocked) {
                document.body.classList.add(lockClass);
            } else {
                document.body.classList.remove(lockClass);
            }
        }
    }

    _checkShownState(isShown) {
        this._lockModalViewport(isShown);

        if (isShown) {
            this._attachWindowListeners();
        } else {
            this._unattachWindowListeners();
        }
    }

    _attachWindowListeners() {
        addWindowListener('keydown', this._boundHandleEscKey);
    }

    _unattachWindowListeners() {
        removeWindowListener('keydown', this._boundHandleEscKey);
    }

    _handleClose() {
        if (this.props.onClose) {
            this.props.onClose();
        }
    }

    _handleEscKey(event) {
        if (event.key === 'Escape') {
            this._handleClose();
        }
    }

    _handleClickOutside() {
        const { disableCloseOnClickOutside } = this.props;

        if (!disableCloseOnClickOutside) {
            this._handleClose();
        }
    }

    render() {
        const {
            children,
            type,
            isShown,
            noMinHeight,
            title,
            secondaryTitle,
            illustration,
            pane,
            isPaneOpen,
            headerActionIconType,
            headerActionTitle,
            onHeaderAction,
            primarySize,
            primaryType,
            primaryText,
            primaryIsDisabled,
            onPrimaryAction,
            secondaryType,
            secondaryText,
            secondaryIsDisabled,
            onSecondaryAction,
            tertiaryNode,
            onTertiaryAction,
            onClose,
            noPadding,
            dangerouslySetContainerClassName,
            modalBodyIsEmpty,
            notificationOptions,
            roleIsDialog,
            ariaLabel,
            ariaLabelledby,
            ariaDescribedby,
            ariaModal,
            buttonBarMessage,
            primaryIsLoading,
        } = this.props;

        if (!isShown) {
            return null;
        }

        const modalClasses = classNames('eds-modal', 'eds-fx--fade-in', {
            [`eds-modal--${type}`]: type,
            'eds-modal--active': isShown,
            'eds-modal--has-padding': !noPadding,
            'eds-modal--set-height': !noMinHeight,
        });

        // We need to set a fallbackFocus in case there was no inital foucsed element
        // https://github.com/davidtheclark/focus-trap/blob/master/index.js#L156
        const focusTrapOptions = { fallbackFocus: () => noop };
        let accessibilityProps = {
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledby,
            'aria-describedby': ariaDescribedby,
            'aria-modal': ariaModal,
            role: 'alertdialog',
        };

        // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-labelledby_attribute
        if (title && !ariaLabelledby) {
            accessibilityProps = {
                ...accessibilityProps,
                'aria-labelledby': constants.MODAL_TITLE_ID,
            };
        }

        // Default to alertdialog role type unless specifically dialog type is requested
        // https://www.w3.org/TR/wai-aria-1.1/#alertdialog & https://www.w3.org/TR/wai-aria-1.1/#dialog
        if (roleIsDialog) {
            accessibilityProps = { ...accessibilityProps, role: 'dialog' };
        }

        return (
            <FocusTrap focusTrapOptions={focusTrapOptions}>
                <div
                    className={modalClasses}
                    data-spec="eds-modal"
                    {...accessibilityProps}
                >
                    <DecoratedModalBody
                        onClose={onClose}
                        primarySize={primarySize}
                        primaryType={primaryType}
                        primaryText={primaryText}
                        primaryIsDisabled={primaryIsDisabled}
                        primaryIsLoading={primaryIsLoading}
                        onPrimaryAction={onPrimaryAction}
                        secondaryType={secondaryType}
                        secondaryText={secondaryText}
                        secondaryIsDisabled={secondaryIsDisabled}
                        tertiaryNode={tertiaryNode}
                        type={type}
                        onTertiaryAction={onTertiaryAction}
                        onSecondaryAction={onSecondaryAction}
                        headerActionIconType={headerActionIconType}
                        headerActionTitle={headerActionTitle}
                        onHeaderAction={onHeaderAction}
                        title={title}
                        secondaryTitle={secondaryTitle}
                        illustration={illustration}
                        pane={pane}
                        isPaneOpen={isPaneOpen}
                        onClickOutside={this._handleClickOutside.bind(this)}
                        dangerouslySetContainerClassName={
                            dangerouslySetContainerClassName
                        }
                        modalBodyIsEmpty={modalBodyIsEmpty}
                        notificationOptions={notificationOptions}
                        buttonBarMessage={buttonBarMessage}
                    >
                        {children}
                    </DecoratedModalBody>
                </div>
            </FocusTrap>
        );
    }
}
